import setPath from 'lodash/set';
import { createStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import isEqual from 'lodash/isEqual';

import { transformUrlSlugToTitle } from '../utils/UrlFormatters';
import { stripSlashes } from '../utils/StringFormatters';
import { SITEMAP_LOCAL_STORAGE_KEY } from './sitemap_lib';

/**
 * Helper functions
 */
const createPageMap = (pages) => Object.fromEntries(pages.map((page) => [page.url_slug === '/' ? page.url_slug : page.url_slug.replace(/^\/+/, ''), page]));

const createCategoryMaps = (categoriesWithTemplates) => Object.fromEntries(categoriesWithTemplates.map((cat) => [cat.url_pattern, cat]));

const createTreeNode = (item, fullPath, isCategory, isNested, isPlaceholder) => ({
  ...item,
  title: isPlaceholder ? transformUrlSlugToTitle(fullPath) : item.title,
  isPlaceholder,
  isCategory,
  isNested,
  url_slug: fullPath,
  children: [],
});

const buildTree = (pageMap, categoryMap, pages, withIndex) => {
  const urlsTree = [];
  const sortedPages = [...pages].sort((a, b) => stripSlashes(a.url_slug).localeCompare(stripSlashes(b.url_slug)));

  for (const page of sortedPages) {
    const urlParts = page.url_slug.replace(/^\/+/, '').split('/').filter(Boolean);
    let currentLevel = urlsTree;
    let fullPath = '';

    for (const part of urlParts) {
      fullPath = fullPath ? `${fullPath}/${part}` : part;
      let existingPath = currentLevel.find((level) => level.url_slug === fullPath);

      if (existingPath) {
        currentLevel = existingPath.children;
      } else {
        const urlSlug = urlParts[0];
        const isCategory = !!categoryMap[fullPath];
        const isNested = categoryMap[urlSlug.startsWith('//') ? '/' : urlSlug]?.is_nested;
        const mapItem = pageMap[fullPath];
        const isPlaceholder = !mapItem;
        const newLevel = createTreeNode(mapItem, fullPath, isCategory, isNested, isPlaceholder);
        currentLevel.push(newLevel);
        currentLevel = newLevel.children;
      }
    }
  }

  if (withIndex) {
    const indexPage = pages.find((page) => page.url_slug === '/');
    if (indexPage) {
      urlsTree.unshift({
        ...indexPage,
        children: [],
        ...(pageMap['/'] || {}),
      });
    }
  }

  return urlsTree;
};

const getCount = (pages) => {
  const count = { live: 0, draft: 0, category: 0, builder: 0, job: 0 };

  pages.forEach((page) => {
    if (page.is_from_sitemap) count.live += 1;
    if (page.is_draft) count.draft += 1;
    if (page.parent_category_id) count.category += 1;
    if (page.is_dynamic_page) count.builder += 1;
    if (page.is_job) count.job += 1;
  });

  return { ...count, pending: pages.length - count.live - count.draft, codebase: pages.length - count.category - count.builder };
};

const shouldIncludePage = (page, hide) => {
  if (hide.categories && page.parent_category_id) return false;
  if (hide.builder && page.is_dynamic_page) return false;
  if (hide.codebase && page.is_from_sitemap && !page.is_dynamic_page && !page.parent_category_id) return false;
  if (hide.live && page.is_from_sitemap) return false;
  if (hide.draft && page.is_draft) return false;
  if (hide.pending && !page.is_from_sitemap && !page.is_draft) return false;

  return true;
};
/**
 * End of Helper Functions
 */

const INITIAL_STATE = {
  isLoading: true,
  isError: false,
  status: '',
  search: '',
  pages: [], // This will hold the final list/tree for the view
  statusCount: {},
  view: 0,
  hide: { categories: false, builder: false, sitemap: false, live: false, pending: false, draft: false },
  sort: { key: 0, order: 0 },
  expandedKeys: {},
};

const createSitemapStoreBase = (initialState, allSitePages = [], categoriesWithTemplates = []) => {
  const store = createStore(
    subscribeWithSelector(
      immer((set, get) => ({
        ...initialState,
        allSitePages,
        pageMap: createPageMap(allSitePages),
        categoryMap: createCategoryMaps(categoriesWithTemplates),

        update: (updates) =>
          set((state) => {
            if (typeof updates === 'object' && !Array.isArray(updates)) {
              for (const [path, value] of Object.entries(updates)) {
                setPath(state, path, value);
              }
            } else {
              throw new Error('update must receive only ONE(1) parameter which must be an object with key-value pairs');
            }
          }),

        setExpandedKeys: (keys) => set((state) => ({ expandedKeys: { ...state.expandedKeys, ...keys } })),

        calculatePages: () => {
          set({ isLoading: true });

          const { search, hide, sort, view, allSitePages, pageMap, categoryMap } = get();

          const searchKeyword = search.toLowerCase();
          let filteredPages = allSitePages.filter(
            (page) =>
              shouldIncludePage(page, hide) &&
              ((page.title?.toLowerCase() || '').includes(searchKeyword) || (page.url_slug?.toLowerCase() || '').includes(searchKeyword)),
          );

          const getSafe = (obj, prop) => (obj?.[prop] ?? '').toString().toLowerCase();
          const sortedPages = [...filteredPages].sort((a, b) => {
            switch (sort.key) {
              case 1: // URL Slug
                return sort.order === 0
                  ? stripSlashes(getSafe(a, 'url_slug')).localeCompare(stripSlashes(getSafe(b, 'url_slug')))
                  : stripSlashes(getSafe(b, 'url_slug')).localeCompare(stripSlashes(getSafe(a, 'url_slug')));
              case 2: // Updated At
                return sort.order === 0
                  ? getSafe(a, 'updated_at').localeCompare(getSafe(b, 'updated_at'))
                  : getSafe(b, 'updated_at').localeCompare(getSafe(a, 'updated_at'));
              default: // Title
                return sort.order === 0 ? getSafe(a, 'title').localeCompare(getSafe(b, 'title')) : getSafe(b, 'title').localeCompare(getSafe(a, 'title'));
            }
          });

          const statusCount = getCount(sortedPages);

          let pages;

          if (view === 0) {
            const withIndex = search === '' && !hide.codebase && !hide.live;
            pages = buildTree(pageMap, categoryMap, filteredPages, withIndex);
          } else {
            pages = sortedPages;
          }

          set({ pages, statusCount, isLoading: false });
        },
      })),
    ),
  );

  store.getState().calculatePages();

  store.subscribe(
    (state) => [state.search, state.hide, state.sort, state.view],
    () => store.getState().calculatePages(),
    { equalityFn: isEqual },
  );

  return store;
};

export const createSitemapStore = (initialState = INITIAL_STATE, allSitePages, categoriesWithTemplates) => {
  try {
    const stored = localStorage.getItem(SITEMAP_LOCAL_STORAGE_KEY);
    const storedState = stored ? JSON.parse(stored) : {};
    return createSitemapStoreBase({ ...initialState, ...storedState }, allSitePages, categoriesWithTemplates);
  } catch (e) {
    console.error('[SFPB - CMS] Error creating sitemap store:', e);
    return createSitemapStoreBase(initialState, allSitePages, categoriesWithTemplates);
  }
};
